import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';

import {
    Checkbox,
    Spacer,
    Switch,
    TextButton,
} from '@zillow/constellation';
import { Col, Container, Row } from '@zillow/zrs-comet';

import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import PostingActions from 'app/shared/actions/PostingActions';
import TourCardEditable from 'app/shared/components/postingPath/TourCardEditable';
import TourCardEditableHorizontal from 'app/shared/components/postingPath/TourCardEditableHorizontal';

import {
    StyledControlsSpacer,
    StyledEmphasizeText,
    StyledSwitchContainerDiv,
    StyledText,
    StyledUnitCardDiv,
} from './StyledComponents';

const UnitCard = ({
    canEditTourOverwrite,
    floorPlanIndex,
    formData,
    id,
    isDisabled,
    isMobile,
    onRemoveUnit,
    onToggleActive,
    unit,
    updateFormData,
    updateParentFloorplanPrice,
    unitIndex,
    updateStatusMessage,
}) => {
    const dispatch = useDispatch();

    const handleEditUnit = () => {
        const floorplan = formData.floorplans[floorPlanIndex];
        const unitIndexToEdit = findIndex(floorplan.units, existingUnit => existingUnit.unitId === unit.unitId);
        if (unitIndexToEdit === -1) {
            return;
        }

        dispatch(ModalActions.showModal({
            modalType: ModalConstants.EDIT_UNIT_MODAL,
            header: 'Edit unit',
            mode: 'edit',
            unit,
            defaultUnit: unit,
            isFluid: true,
            onSubmit: params => dispatch(PostingActions.property.unit.edit(unit.unitId, params))
                .then(() => {
                    const newFloorplans = cloneDeep(formData.floorplans);
                    newFloorplans[floorPlanIndex].units[unitIndexToEdit] = {
                        ...newFloorplans[floorPlanIndex].units[unitIndexToEdit],
                        ...params,
                    };
                    updateFormData({ floorplans: newFloorplans });
                    if (params.price !== newFloorplans.price) {
                        updateParentFloorplanPrice(formData.floorplans[floorPlanIndex].floorPlanId);
                    }
                    dispatch(ModalActions.hideModal());
                }),
            onCancel: () => dispatch(ModalActions.hideModal()),
        }));
    };

    const handleRemoveUnit = () => {
        if (!isDisabled) {
            onRemoveUnit();
        }
    };
    const onKeyUp = (event) => {
        if (event.keyCode === KeyCodeConstants.ENTER) {
            event.target.click();
        }
    };

    const date = unit.dateAvailable ? format(unit.dateAvailable, 'MM/DD/YYYY') : '-';
    const TourCardComponent = isMobile ? TourCardEditable : TourCardEditableHorizontal;
    return (
        <StyledUnitCardDiv className="UnitCard">
            <Spacer padding="sm">
                <Container>
                    <Row paddingY="xs">
                        <Col
                            xs={12}
                            sm={6}
                        >
                            <StyledText
                                fontType="bodyHeading"
                            >
                                {unit.name || 'Unit name'}
                            </StyledText>
                        </Col>
                        <Col
                            xs={12}
                            sm={6}
                        >
                            <StyledSwitchContainerDiv>
                                <StyledEmphasizeText
                                    fontType="body"
                                >
                                Unit available
                                </StyledEmphasizeText>
                                <Spacer marginLeft="sm">
                                    <Switch
                                        controlId={`Switch-${id}`}
                                        checkbox={(
                                            <Checkbox
                                                aria-label="Unit available"
                                                checked={unit.active}
                                                onClick={onToggleActive}
                                            />
                                        )}
                                        disabled={isDisabled}
                                    />
                                </Spacer>
                            </StyledSwitchContainerDiv>
                        </Col>
                    </Row>
                    <Row paddingY="xs">
                        <Col>
                            <StyledText
                                fontType="body"
                            >
                            Price&nbsp;
                                <b>{`$${unit.price || '-'}`}</b>
                            &nbsp;| Sqft&nbsp;
                                <b>{unit.sqft || '-'}</b>
                            &nbsp;| Avail.&nbsp;
                                <b>{date}</b>
                            </StyledText>
                        </Col>
                    </Row>
                    <Row paddingY="xs">
                        <Col>
                            <TourCardComponent
                                tour={unit.tour}
                                tourType="unit"
                                hasBigBottomBorder={false}
                                isDisabled={canEditTourOverwrite ? false : isDisabled}
                                floorPlanIndex={floorPlanIndex}
                                unitIndex={unitIndex}
                                updateStatusMessage={updateStatusMessage}
                                updateFormData={updateFormData}
                                formData={formData}
                            />
                        </Col>
                    </Row>
                </Container>
            </Spacer>
            {!isDisabled && (
                <StyledControlsSpacer paddingY="sm">
                    <Spacer marginLeft="xs" as="span">
                        <TextButton
                            aria-label="Edit unit"
                            tabIndex={0}
                            onClick={handleEditUnit}
                            onKeyUp={onKeyUp}
                        >
                            Edit
                        </TextButton>
                    </Spacer>
                    <Spacer marginLeft="xs" as="span">
                        <TextButton
                            aria-label="Delete unit"
                            tabIndex={0}
                            onClick={handleRemoveUnit}
                            onKeyUp={onKeyUp}
                        >
                            Delete
                        </TextButton>
                    </Spacer>
                </StyledControlsSpacer>
            )}
        </StyledUnitCardDiv>
    );
};

UnitCard.propTypes = {
    unit: PropTypes.shape({
        name: PropTypes.string,
        sqft: PropTypes.string,
        dateAvailable: PropTypes.string,
        price: PropTypes.number,
        active: PropTypes.bool,
        tour: PropTypes.shape({
            name: PropTypes.string.isRequired,
            createdDate: PropTypes.string.isRequired,
            creatorName: PropTypes.string.isRequired,
            imageSrc: PropTypes.string,
            state: PropTypes.oneOf(['active', 'inactive', 'empty']),
        }).isRequired,
    }).isRequired,
    isMobile: PropTypes.bool,
    isDisabled: PropTypes.bool,
    canEditTourOverwrite: PropTypes.bool,
    onToggleActive: PropTypes.func.isRequired,
    onRemoveUnit: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    floorPlanIndex: PropTypes.number.isRequired,
    formData: PropTypes.object.isRequired,
    updateFormData: PropTypes.func.isRequired,
    updateStatusMessage: PropTypes.func.isRequired,
};

UnitCard.defaultProps = {
    isDisabled: false,
    canEditTourOverwrite: true,
    isMobile: false,
};

export default UnitCard;
